"use client";

import * as React from "react";
import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";
import { SelectBlock } from "@/src/components/input/select-advanced";
import { InstallCommand } from "@/components/docs/install-command";

const basicOptions = [
  { label: "Option 1", value: "1" },
  { label: "Option 2", value: "2" },
  { label: "Option 3", value: "3" },
];

const groupedOptions = [
  {
    label: "Fruits",
    options: [
      { label: "Apple", value: "apple" },
      { label: "Banana", value: "banana" },
      { label: "Orange", value: "orange" },
    ],
  },
  {
    label: "Vegetables",
    options: [
      { label: "Carrot", value: "carrot" },
      { label: "Broccoli", value: "broccoli" },
    ],
  },
];

export default function DocsComponentsSelect() {
  const [value, setValue] = React.useState<{ label: string; value: string } | null>(null);
  const [multiValue, setMultiValue] = React.useState<{ label: string; value: string }[]>([]);

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Select
            </Text>
            <Badge variant="secondary">Form</Badge>
          </div>
          <Text as="p" className="text-lg text-muted-foreground max-w-2xl">
            A dropdown select component built on react-select with support for
            single/multiple selection, grouped options, and virtualization.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/select" />
        </SectionCard>
      </Section>

      {/* Basic Example */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Basic Select</CardTitle>
              <CardDescription>Single selection dropdown</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="max-w-sm">
            <SelectBlock
              label="Choose an option"
              options={basicOptions}
              value={value}
              onChange={(newValue) => setValue(newValue as { label: string; value: string } | null)}
              placeholder="Select..."
            />
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code>{`<SelectBlock
  label="Choose an option"
  options={options}
  value={value}
  onChange={setValue}
  placeholder="Select..."
/>`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Multiple Selection */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Multiple Selection</CardTitle>
              <CardDescription>Allow selecting multiple options</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="max-w-sm">
            <SelectBlock
              label="Select multiple"
              options={basicOptions}
              value={multiValue}
              onChange={(newValue) => setMultiValue(newValue as { label: string; value: string }[])}
              multiple
              placeholder="Select options..."
            />
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code>{`<SelectBlock
  label="Select multiple"
  options={options}
  multiple
  value={multiValue}
  onChange={setMultiValue}
/>`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Grouped Options */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Grouped Options</CardTitle>
              <CardDescription>Organize options into groups</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="max-w-sm">
            <SelectBlock
              label="Select item"
              options={groupedOptions}
              placeholder="Choose from groups..."
            />
          </div>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <code>{`const groupedOptions = [
  {
    label: "Fruits",
    options: [
      { label: "Apple", value: "apple" },
      { label: "Banana", value: "banana" },
    ],
  },
  {
    label: "Vegetables",
    options: [
      { label: "Carrot", value: "carrot" },
    ],
  },
];

<SelectBlock options={groupedOptions} />`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* With Validation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>With Validation</CardTitle>
              <CardDescription>Show help text and error states</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SelectBlock
              label="With help text"
              options={basicOptions}
              helpText="Choose the option that best fits your needs"
              placeholder="Select..."
            />
            <SelectBlock
              label="With error"
              options={basicOptions}
              error="This field is required"
              required
              placeholder="Select..."
            />
          </div>
        </SectionCard>
      </Section>

      {/* Props */}
      <Section>
        <SectionCard
          header={<CardTitle>Props</CardTitle>}
          contentClassName="p-6"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 font-semibold">Prop</th>
                <th className="text-left py-2 font-semibold">Type</th>
                <th className="text-left py-2 font-semibold">Default</th>
                <th className="text-left py-2 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">options</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">SelectOption[]</td>
                <td className="py-2 font-mono text-xs">required</td>
                <td className="py-2 text-muted-foreground">Array of options or grouped options</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">value</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">Option | Option[] | null</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Selected value(s)</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">onChange</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">(value) =&gt; void</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Change handler</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">multiple</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Enable multi-select</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">virtualised</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Use virtualized list for large datasets</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">disabled</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">boolean</td>
                <td className="py-2 font-mono text-xs">false</td>
                <td className="py-2 text-muted-foreground">Disable the select</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">label</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Field label</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">helpText</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Help text below the field</td>
              </tr>
              <tr className="border-b">
                <td className="py-2 font-mono text-xs">error</td>
                <td className="py-2 font-mono text-xs text-muted-foreground">ReactNode</td>
                <td className="py-2 font-mono text-xs">-</td>
                <td className="py-2 text-muted-foreground">Error message</td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      </Section>
    </>
  );
}
